import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import { Tooltip, IconButton } from "@material-ui/core";
import CustomModal from "../CustomModal";
import CustomButton from "../CustomButton";
import { ButtonLoader } from "../ButtonLoader";
import ContactResultsList from "./ContactResultsList";
import InviteConfirmModal from "./InviteConfirmModal";
import contactImportService from "../../services/contactImportService";
import { ReactComponent as GoogleIcon } from "../../assets/icons/google-icon.svg";
import { IMPORT_HOW_IT_WORKS } from "../../utils/growNetwork";
import {
  trackContactsImportStarted,
  trackContactsImportCompleted,
  trackInvitesSent,
  trackMilestoneReached,
} from "../../utils/analytics";

const STEPS = {
  SOURCE: "source",
  LOADING: "loading",
  RESULTS: "results",
  SUCCESS: "success",
};

const ImportContactsModal = ({
  isOpen,
  onClose,
  returnPath = "/home",
  initialStep,
  onComplete,
  startWithCsv = false,
}) => {
  const { userData } = useSelector((s) => s.auth);
  const [step, setStep] = useState(STEPS.SOURCE);
  const [contacts, setContacts] = useState({ on_platform: [], invite: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [prevMilestone, setPrevMilestone] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedIds([]);
    setInviteResult(null);
    setSearchQuery("");

    if (initialStep === "results") {
      loadContacts();
      return;
    }

    setImportSummary(null);

    if (startWithCsv) {
      setStep(STEPS.SOURCE);
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }, 300);
    } else {
      setStep(STEPS.SOURCE);
    }
  }, [isOpen, initialStep, startWithCsv]);

  const loadContacts = async () => {
    setStep(STEPS.LOADING);
    try {
      const res = await contactImportService.getContacts();
      const data = res?.data ?? res;
      setContacts({
        on_platform: data?.on_platform || [],
        invite: data?.invite || [],
      });
      setStep(STEPS.RESULTS);
    } catch (e) {
      cogoToast.error("Failed to load contacts");
      setStep(STEPS.SOURCE);
    }
  };

  const handleGoogle = async () => {
    trackContactsImportStarted("google");
    try {
      const res = await contactImportService.getGoogleAuthUrl(returnPath);
      const authUrl = res?.data?.auth_url || res?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        cogoToast.error("Could not start Google import");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message || "Could not start Google import";
      cogoToast.error(msg);
    }
  };

  const handleCsvPick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCsvChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      cogoToast.error("File exceeds maximum size of 2MB");
      return;
    }

    trackContactsImportStarted("csv");
    setStep(STEPS.LOADING);
    try {
      const res = await contactImportService.importCsv(file);
      const data = res?.data ?? res;
      setImportSummary(data);
      trackContactsImportCompleted("csv", data?.imported || 0);

      if (data?.message) {
        cogoToast.success(data.message);
      }

      const contactsRes = await contactImportService.getContacts();
      const cData = contactsRes?.data ?? contactsRes;
      setContacts({
        on_platform: cData?.on_platform || [],
        invite: cData?.invite || [],
      });

      const statsRes = await contactImportService.getStats();
      const stats = statsRes?.data ?? statsRes;
      maybeFireMilestones(stats);

      setStep(STEPS.RESULTS);
      if (onComplete) onComplete(stats);
    } catch (err) {
      cogoToast.error(
        err?.response?.data?.message || "CSV import failed"
      );
      setStep(STEPS.SOURCE);
    }
  };

  const maybeFireMilestones = (stats) => {
    if (!stats) return;
    const current = stats.current_milestone || 0;
    if (current > prevMilestone && current > 0) {
      trackMilestoneReached(current);
    }
    setPrevMilestone(current);
  };

  const toggleId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredContacts = {
    on_platform: (contacts.on_platform || []).filter((c) => {
      if (!normalizedSearch) return true;
      const label =
        `${c.name || ""} ${c.email || ""} ${c.phone || ""} ${c.profile?.first_name || ""} ${c.profile?.last_name || ""}`.toLowerCase();
      return label.includes(normalizedSearch);
    }),
    invite: (contacts.invite || []).filter((c) => {
      if (!normalizedSearch) return true;
      const label = `${c.name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
      return label.includes(normalizedSearch);
    }),
  };

  const toggleAllInvite = (selectAll) => {
    const inviteable = (filteredContacts.invite || []).filter(
      (c) => !c.invite_blocked && !c.already_invited_by_self
    );
    setSelectedIds(selectAll ? inviteable.map((c) => c.id) : []);
  };

  const handleSendConfirm = async (message = {}) => {
    setSending(true);
    try {
      const res = await contactImportService.sendInvites(selectedIds, message);
      const data = res?.data ?? res;

      if (res?.status === false || data?.error === "rate_limited") {
        cogoToast.error(res?.message || data?.message || "Rate limit reached");
        setSending(false);
        return;
      }

      trackInvitesSent(data?.sent_count || selectedIds.length);
      setInviteResult(data);

      const statsRes = await contactImportService.getStats();
      const stats = statsRes?.data ?? statsRes;
      maybeFireMilestones(stats);

      setConfirmOpen(false);
      setStep(STEPS.SUCCESS);
      if (onComplete) onComplete(stats);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        cogoToast.error(
          err?.response?.data?.message || "Daily invite limit reached"
        );
      } else {
        cogoToast.error(
          err?.response?.data?.message || "Failed to send invites"
        );
      }
    } finally {
      setSending(false);
    }
  };

  const inviterName = userData
    ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim()
    : "";

  const selectedContacts = (contacts.invite || []).filter((c) =>
    selectedIds.includes(c.id)
  );
  const emailInviteCount = selectedContacts.filter((c) => !!c.email).length;
  const smsInviteCount = selectedContacts.filter(
    (c) => !c.email && !!c.phone
  ).length;

  const titleMap = {
    [STEPS.SOURCE]: "Import Contacts",
    [STEPS.LOADING]: "Importing...",
    [STEPS.RESULTS]: "Preview contacts",
    [STEPS.SUCCESS]: "Invites Sent!",
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title={titleMap[step]}
        titleExtra={
          step === STEPS.SOURCE ? (
            <Tooltip
              arrow
              placement="bottom-start"
              interactive
              title={
                <span style={{ display: "block", maxWidth: 280, lineHeight: 1.5 }}>
                  {IMPORT_HOW_IT_WORKS}
                </span>
              }
            >
              <IconButton
                size="small"
                aria-label="How importing contacts works"
                className="gn-info-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="#3A6ED4" strokeWidth="2" />
                  <path d="M12 10.5V17" stroke="#3A6ED4" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="7.25" r="1.1" fill="#3A6ED4" />
                </svg>
              </IconButton>
            </Tooltip>
          ) : null
        }
        maxWidth={step === STEPS.RESULTS ? "800px" : "520px"}
      >
        <div className="gn-import-modal">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={handleCsvChange}
          />

          {step === STEPS.SOURCE && (
            <div className="gn-source-options">
              <button type="button" className="gn-source-btn" onClick={handleGoogle}>
                <span className="gn-source-icon" aria-hidden="true">
                  <GoogleIcon />
                </span>
                <span>
                  <strong>Google Contacts</strong>
                  <span>Import from your Gmail / Google account</span>
                </span>
              </button>
              <button type="button" className="gn-source-btn" onClick={handleCsvPick}>
                <strong>Upload CSV</strong>
                <span>Columns: name, email, phone — max 2MB / 5,000 rows</span>
              </button>
              <a
                href={`${process.env.PUBLIC_URL || ""}/contact-import-template.csv`}
                download="contact-import-template.csv"
                className="small text-center"
                style={{ color: "#3A6ED4" }}
              >
                Download CSV template
              </a>
            </div>
          )}

          {step === STEPS.LOADING && (
            <div className="gn-loading">
              <ButtonLoader />
              <p>Checking your contacts against Workbrook...</p>
            </div>
          )}

          {step === STEPS.RESULTS && (
            <>
              <p className="mb-2" style={{ color: "#334155", fontSize: 14 }}>
                Review your imported contacts below. Select who to invite, then
                click <strong>Send Invites</strong>. Nothing is sent until you
                confirm. Contacts with email get an email; phone-only contacts
                get an SMS.
              </p>
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control gn-contacts-search"
                  placeholder="Search by name, email, or phone"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <p className="small text-muted mb-3">
                {(contacts.on_platform?.length || 0) +
                  (contacts.invite?.length || 0)}{" "}
                contact
                {(contacts.on_platform?.length || 0) +
                  (contacts.invite?.length || 0) ===
                1
                  ? ""
                  : "s"}{" "}
                ready to review
                {importSummary?.imported
                  ? ` · ${importSummary.imported} new from this import`
                  : ""}
                {importSummary?.skipped_duplicates
                  ? ` · ${importSummary.skipped_duplicates} already saved`
                  : ""}
              </p>
              <ContactResultsList
                onPlatform={filteredContacts.on_platform}
                invite={filteredContacts.invite}
                selectedIds={selectedIds}
                onToggle={toggleId}
                onToggleAllInvite={toggleAllInvite}
              />
              <div className="gn-actions-row mt-3">
                <div className="gn-action-left">
                  <CustomButton
                    text="Import More"
                    backgroundColor="#fff"
                    color="#3A6ED4"
                    borderColor="#3A6ED4"
                    handleClick={() => setStep(STEPS.SOURCE)}
                    additionalClass="gn-import-more-btn"
                  />
                </div>
                <div className="gn-action-right d-flex flex-column align-items-stretch align-items-md-end">
                  {selectedIds.length === 0 && (
                    <span
                      className="small text-muted mb-1 text-md-right"
                      style={{ minHeight: 36 }}
                    >
                      Select contacts on the right to enable Send Invites
                    </span>
                  )}
                  <CustomButton
                    additionalClass="gn-send-invites-btn"
                    text={
                      selectedIds.length
                        ? `Send Invites (${selectedIds.length})`
                        : "Send Invites"
                    }
                    backgroundColor={
                      selectedIds.length === 0 ? "#CBD5E1" : "#3A6ED4"
                    }
                    color="#fff"
                    borderColor={
                      selectedIds.length === 0 ? "#CBD5E1" : "#3A6ED4"
                    }
                    handleClick={() => setConfirmOpen(true)}
                    disabled={selectedIds.length === 0}
                  />
                </div>
              </div>
            </>
          )}

          {step === STEPS.SUCCESS && (
            <div className="gn-success">
              <div style={{ fontSize: 48 }}>🎉</div>
              <h4>
                {inviteResult?.sent_count || 0} invite
                {(inviteResult?.sent_count || 0) === 1 ? "" : "s"} sent!
              </h4>
              <p className="text-muted">
                Recipients will get an email or SMS from Workbrook with your
                invite link, depending on the contact method available.
              </p>
              <CustomButton
                text="Done"
                backgroundColor="#3A6ED4"
                color="#fff"
                handleClick={onClose}
                additionalClass="mt-3"
              />
            </div>
          )}
        </div>
      </CustomModal>

      <InviteConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSendConfirm}
        count={selectedIds.length}
        inviterName={inviterName}
        loading={sending}
        emailCount={emailInviteCount}
        smsCount={smsInviteCount}
      />
    </>
  );
};

ImportContactsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  returnPath: PropTypes.string,
  initialStep: PropTypes.string,
  onComplete: PropTypes.func,
  startWithCsv: PropTypes.bool,
};

export default ImportContactsModal;
